import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Autocomplete,
  Box,
  CircularProgress
} from '@mui/material';
import { apiClient } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorUtils';

interface User {
  uuid: string;
  name: string;
  email: string;
}

interface Country {
  code: string;
  name: string;
}

interface OrganizationCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onOrganizationCreated: () => void;
}

export default function OrganizationCreateDialog({
  open,
  onClose,
  onOrganizationCreated
}: OrganizationCreateDialogProps) {
  const [name, setName] = useState('');
  const [selectedOwner, setSelectedOwner] = useState<User | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadData();
    } else {
      // Reset form when dialog closes
      setName('');
      setSelectedOwner(null);
      setSelectedCountry(null);
      setError(null);
    }
  }, [open]);

  const loadData = async () => {
    try {
      setLoadingData(true);
      setError(null);
      
      // Load users and countries in parallel
      const [usersData, countriesData] = await Promise.all([
        apiClient.get<User[]>('/admin/users'),
        apiClient.get<Country[]>('/admin/organizations/countries')
      ]);
      
      // Filter to only show regular users (not service accounts)
      const regularUsers = usersData.filter((user: any) => user.type === 'USER');
      setUsers(regularUsers);
      setCountries(countriesData);
      
      // Pre-select New Zealand as default country
      const newZealand = countriesData.find((c: Country) => c.code === 'NZ');
      if (newZealand) {
        setSelectedCountry(newZealand);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Organisation name is required');
      return;
    }
    
    if (!selectedOwner) {
      setError('Owner is required');
      return;
    }
    
    if (!selectedCountry) {
      setError('Country is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await apiClient.post('/admin/organizations', {
        name: name.trim(),
        owner_uuid: selectedOwner.uuid,
        primary_country_code: selectedCountry.code
      });

      onOrganizationCreated();
      onClose();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to create organisation'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Organisation</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loadingData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TextField
                label="Organisation Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                autoFocus
              />

              <Autocomplete
                options={users}
                getOptionLabel={(option) => `${option.name} (${option.email})`}
                value={selectedOwner}
                onChange={(_, newValue) => setSelectedOwner(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Owner" required />
                )}
                isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
              />

              <Autocomplete
                options={countries}
                getOptionLabel={(option) => `${option.name} (${option.code})`}
                value={selectedCountry}
                onChange={(_, newValue) => setSelectedCountry(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Country" required />
                )}
                isOptionEqualToValue={(option, value) => option.code === value.code}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || loadingData}
        >
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}